import { isEmpty } from 'lodash';
import { useEffect, useState } from 'react';
import type { DefaultLanguage } from '@common/translate/translate';
import type { IPractitioner } from '@frontend/components/interfaces/practitioner';
import type { AdminProcess } from '@frontend/components/pages/processes/adminProcessDetailsTypes';
import { ManagerAuthorization, ProcedureStatus, SomnologyOrganizationType, ThirdPartyName } from '@common/enums';
import { getDisplayName } from '@common/utils/getDisplayName';
import { watchpatSleepRecorderTypes } from '@common/validation/customControls/somnology';
import {
    AdminTable,
    Button,
    ButtonContainer,
    FormDivider,
    FormParagraph,
    InfoTooltip,
    TableBody,
    TableData,
    TableHead,
    TableHeader,
    TableRow,
    UserTextParagraph,
} from '@frontend/components/elements/common';
import { FormLabel } from '@frontend/components/elements/forms/FormLabel';
import { HorizontalFormGroupRead } from '@frontend/components/elements/forms/horizontalFormGroupRead/HorizontalFormGroupRead';
import { InputContainer } from '@frontend/components/elements/forms/InputContainer';
import { NullableFormattedDatetime } from '@frontend/components/elements/NullableFormattedDatetime';
import { fetchJson, getJson } from '@frontend/store/fetch';
import { getSelectedLanguage } from '@frontend/translate';
import { hasOneAuthorization } from '@frontend/utils';
import { WatchPatSerialNumber } from '../../../../elements/WatchPatSerialNumber';

interface ICustomControlData {
    ectosenseActivationCode: {
        type: string;
    };
    manuallyCompletedByPractitionerId: string;
    nightlyReportMetadata: {
        [key: string]: {
            endDatetime: string;
            failureCode?: string;
            startDatetime: string;
            type: string;
        };
    };
    nightlyReports: string[];
    summaryReportMetadata: {
        type: string;
    };
}

export function OsasvoV1AdminDetails(props: AdminProcess & { reload: () => Promise<void> }) {
    const [completedByPractitioner, setCompletedByPractitioner] = useState<IPractitioner | undefined>();
    const [ectosenseTestId, setEctosenseTestId] = useState<string>();

    if (
        !props.customControlData ||
        isEmpty(props.customControlData) ||
        props.customControlData.somnologyOrganizationType !== SomnologyOrganizationType.ksyosMedicalCenter
    ) {
        return null;
    }

    useEffect(() => {
        loadEctosenseTestId();
    }, []);

    useEffect(() => {
        loadPractitioner();
    }, [props.procedure?.customControlData]);

    const customControlData: ICustomControlData = props.procedure?.customControlData;

    return (
        <>
            <FormDivider />
            {props.customControlData.ectosenseActivationCode && (
                <HorizontalFormGroupRead>
                    <FormLabel>Activatiecode</FormLabel>
                    <InputContainer>
                        <UserTextParagraph>{props.customControlData.ectosenseActivationCode}</UserTextParagraph>
                    </InputContainer>
                </HorizontalFormGroupRead>
            )}

            {ectosenseTestId && (
                <HorizontalFormGroupRead>
                    <FormLabel>Ectosense test id</FormLabel>
                    <InputContainer>
                        <UserTextParagraph>{ectosenseTestId}</UserTextParagraph>
                    </InputContainer>
                </HorizontalFormGroupRead>
            )}

            {props.customControlData.slaaprecorderVerzenden && (
                <HorizontalFormGroupRead>
                    <FormLabel>Slaaprecorder verzenden</FormLabel>
                    <InputContainer>
                        <UserTextParagraph>
                            {
                                getSelectedLanguage().osas[
                                    `${props.customControlData.slaaprecorderVerzenden}AdminDetails` as keyof DefaultLanguage['osas']
                                ]
                            }
                        </UserTextParagraph>
                    </InputContainer>
                </HorizontalFormGroupRead>
            )}

            {watchpatSleepRecorderTypes.includes(props.customControlData.typeSlaaprecorder) && (
                <WatchPatSerialNumber requestId={props.id} />
            )}

            {props.procedure && props.procedure.customControlData && !isEmpty(props.procedure.customControlData) && (
                <>
                    <HorizontalFormGroupRead>
                        <FormLabel>Aantal recordings</FormLabel>
                        <InputContainer>
                            <FormParagraph>3</FormParagraph>
                        </InputContainer>
                    </HorizontalFormGroupRead>

                    <AdminTable>
                        <TableHead>
                            <TableRow>
                                <TableHeader></TableHeader>
                                <TableHeader>Report type</TableHeader>
                                <TableHeader>Startdatum</TableHeader>
                                <TableHeader>Einddatum</TableHeader>
                                <TableHeader>Foutcode</TableHeader>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {customControlData.summaryReportMetadata && (
                                <TableRow>
                                    <TableData>Samenvatting</TableData>
                                    <TableData>{customControlData.summaryReportMetadata.type}</TableData>
                                    <TableData>-</TableData>
                                    <TableData>-</TableData>
                                    <TableData>-</TableData>
                                </TableRow>
                            )}
                            {customControlData.nightlyReports.map((nightlyReportId, index) => {
                                const nightlyReport = customControlData.nightlyReportMetadata[nightlyReportId];

                                return (
                                    <TableRow key={`recordings-${nightlyReportId}`}>
                                        <TableData>Report {index + 1}</TableData>
                                        <TableData>{nightlyReport.type}</TableData>
                                        <TableData>
                                            <NullableFormattedDatetime value={nightlyReport.startDatetime} />
                                        </TableData>
                                        <TableData>
                                            <NullableFormattedDatetime value={nightlyReport.endDatetime} />
                                        </TableData>
                                        <TableData>
                                            {nightlyReport.failureCode}{' '}
                                            {nightlyReport.failureCode ? (
                                                <InfoTooltip
                                                    text={
                                                        failureCodes[nightlyReport.failureCode] ||
                                                        nightlyReport.failureCode
                                                    }
                                                    tooltipHeader={nightlyReport.failureCode}
                                                />
                                            ) : null}
                                        </TableData>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </AdminTable>

                    {props.procedure.customControlData.manuallyCompletedByPractitionerId && completedByPractitioner && (
                        <>Handmatig afgerond door {getDisplayName(completedByPractitioner)}</>
                    )}

                    {!props.procedure.customControlData.manuallyCompletedByPractitionerId &&
                        props.procedure.status === ProcedureStatus.active &&
                        hasOneAuthorization(ManagerAuthorization.adminProcessEdit) && (
                            <ButtonContainer
                                alignRight
                                buttons={[
                                    <Button
                                        color="secondary"
                                        onClick={async () => {
                                            if (props.procedure) {
                                                await fetchJson(
                                                    `/api/procedures/${props.procedure.id}/complete`,
                                                    'POST'
                                                );
                                            }
                                            await props.reload();
                                        }}
                                        outlined
                                    >
                                        Onderzoek afronden
                                    </Button>,
                                ]}
                            />
                        )}
                </>
            )}
        </>
    );

    async function loadPractitioner() {
        if (
            props.procedure &&
            props.procedure.customControlData &&
            props.procedure.customControlData.manuallyCompletedByPractitionerId
        ) {
            const result = await getJson(
                `/api/practitioners/${props.procedure.customControlData.manuallyCompletedByPractitionerId}`
            );
            setCompletedByPractitioner(result.json);
        }
    }

    async function loadEctosenseTestId() {
        const result = await getJson(
            `/api/requests/${props.id}/getThirdPartyReference?thirdPartyName=${ThirdPartyName.ectosense}`
        );

        if (result.json.reference) {
            setEctosenseTestId(result.json.reference);
        }
    }
}

const failureCodes: { [key: string]: string } = {
    BT01: 'There was less than 3 hours of analyzable signal available, the recording cannot be interpreted.',
    ST01: 'There was less than 3 hours of analyzable signal available, the recording cannot be interpreted.',
    R01: 'There was less than 3 hours of analyzable signal available, the recording cannot be interpreted.',
    R02: 'There was less than 3 hours of analyzable signal available, the recording cannot be interpreted.',
    TS01: 'The total recording time was less than 2 hours, we could not perform any analysis. Please advise the patient to record during the entire night.',
    EC01: 'No diagnostic report was generated since the total recording time was less than 3 hours. We noticed that the patient’s smartphone had forced the NightOwl Companion App to shut down. Please advise the patient to use an alternative smartphone or tablet next night.',
    EC02: 'No diagnostic report was generated since the total recording time was less than 3 hours. We noticed that the patient’s smartphone had forced the NightOwl Companion App to shut down. Please advise the patient to use an alternative smartphone or tablet next night.',
    EC03: 'No diagnostic report was generated since the total recording time was less than 3 hours. We noticed issues with the stability of the Bluetooth connection between the patient’s smartphone and the NightOwl sensor. Please advise the patient to either make sure that the phone is within 2 meters of the sensor for the large majority of the night or to use a different smartphone if this was already the case.',
    EC04: 'No diagnostic report was generated since the total recording time was less than 3 hours. We noticed issues with the stability of the Bluetooth connection between the patient’s smartphone and the NightOwl sensor. Please advise the patient to either make sure that the phone is within 2 meters of the sensor for the large majority of the night or to use a different smartphone if this was already the case.',
    EC05: 'No diagnostic report was generated since the total recording time was less than 3 hours.',
    EC06: 'No diagnostic report was generated since the total recording time was less than 3 hours. Please advise the patient to attempt sleeping longer next night.',
    EC07: 'No diagnostic report was generated since there was less than 4 hours of analyzable signal available.',
    EC08: 'No diagnostic report was generated since there was less than 4 hours of analyzable signal available. We noticed issues with the stability of the Bluetooth connection between the patient’s smartphone and the NightOwlsensor. Please advise the patient to either make sure that the phone is within 2 meters of the sensor for the large majority of the night or to use a different smartphone if this was already the case.',
    EC09: 'No diagnostic report was generated since there was less than 4 hours of analyzable signal available. We noticed that the patient’s smartphone hadforced the NightOwl Companion App to shut down. Please advise the patient to use an alternative smartphone or tablet next night.',
    EC10: 'No diagnostic report was generated since there was less than 4 hours of analyzable signal available. We noticed that the patient’s smartphone hadforced the NightOwl Companion App to shut down. Please advise the patient to use an alternative smartphone or tablet next night.',
    EC11: 'The total sleep time was less than 2 hours; hence we could not generate a diagnostic report.\nPlease advise the patient to attempt sleeping longer next night.',
    EC12: 'No diagnostic report was generated since there was less than 4 hours of analyzable signal available. We noticed issues with the stability of the Bluetooth connection between the patient’s smartphone and the NightOwl sensor. Please advise the patient to either make sure that the phone is within 2 meters of the sensor for the large majority of the night or to use a different smartphone if this was already the case.',
};
